export const ADMIN_ROLES = ['manager', 'admin', 'super_admin'] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

export type AdminCapability = 'account:password' | 'admin:workspace' | 'admin:privileged-operator';

// Each entry mirrors the backend guard of the routes behind it, so a screen is
// shown exactly to the roles the API answers. Hiding is a convenience; the API
// check is what protects the data.
const capabilityRoles: Record<AdminCapability, readonly AdminRole[]> = {
  'account:password': ADMIN_ROLES,
  // Every /api/v1/internal/ route: manager, admin or super admin plus
  // operations:read (app/operations/routes.py). The Dashboard is one of them.
  'admin:workspace': ['manager', 'admin', 'super_admin'],
  // infrastructure:read, configuration:manage, destructive:execute
  // (app/core/policy.py grants them to admin and super admin only).
  'admin:privileged-operator': ['admin', 'super_admin'],
};

export function isAdminRole(role: string): role is AdminRole {
  return (ADMIN_ROLES as readonly string[]).includes(role);
}

export function canAccess(role: string | undefined, capability: AdminCapability): boolean {
  return Boolean(role && capabilityRoles[capability].includes(role as AdminRole));
}

export function tokenIsExpired(token: string, now = Date.now()): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/'))) as {
      exp?: unknown;
    };
    return typeof decoded.exp !== 'number' || decoded.exp * 1000 <= now;
  } catch {
    return true;
  }
}

export function invalidatesSession(status: number | undefined): boolean {
  return status === 401;
}

export function resolveApiBaseUrl(value: string, production: boolean): string {
  const url = new URL(value);
  const loopback =
    url.hostname === 'localhost' ||
    url.hostname.endsWith('.localhost') ||
    url.hostname === '127.0.0.1' ||
    url.hostname === '[::1]';
  if (url.username || url.password || url.search || url.hash) {
    throw new Error('API URL must not contain credentials, query parameters, or a hash.');
  }
  if (production && (url.protocol !== 'https:' || loopback)) {
    throw new Error('Production Admin API must use a non-loopback HTTPS origin.');
  }
  return url.toString().replace(/\/$/, '');
}
